import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usersAPI, reviewsAPI } from '../services/api';
import ImageLightbox from './ImageLightbox';

const OwnerClosetPage = ({ onTradeRequest }) => {
  const { ownerId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ average_rating: 0, rating_count: 0, total_items: 0 });
  const [items, setItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [showReviews, setShowReviews] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 12;
  
  // Image lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxItemName, setLightboxItemName] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [closetRes, reviewsRes] = await Promise.all([
          usersAPI.getCloset(ownerId, { page, perPage }),
          reviewsAPI.getUserReviews(ownerId).catch(() => []) // Fallback to empty array if reviews fail
        ]);
        setProfile(closetRes.profile);
        setStats(closetRes.stats);
        setItems(closetRes.items || []);
        setReviews(reviewsRes || []);
      } catch (e) {
        setError(e.message || 'Failed to load closet');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [ownerId, page]);

  // Image lightbox functions
  const openImageLightbox = (item) => {
    const images = [
      item.image_url,
      ...(item.additional_images || [])
    ].filter(Boolean);
    if (images.length > 0) {
      setLightboxImages(images);
      setLightboxIndex(0);
      setLightboxItemName(item.name);
      setLightboxOpen(true);
    }
  };

  if (loading) return <div className="container"><p>Loading closet...</p></div>;
  if (error) return <div className="container"><p style={{color:'#c00'}}>{error}</p></div>;

  const totalPages = Math.max(1, Math.ceil((stats.total_items || 0) / perPage));

  return (
    <div className="closet-page">
      <div className="container">
      <div className="closet-header" style={{display:'flex', gap:16, alignItems:'center', marginBottom:24}}>
        <button 
          className="btn btn-secondary" 
          onClick={() => {
            // Check if there's history to go back to
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              // If no history, go to browse page
              navigate('/browse');
            }
          }} 
          title="Go Back"
        >
          ← Back
        </button>
        <img
          src={profile?.profile_image_url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOTYiIGhlaWdodD0iOTYiIHZpZXdCb3g9IjAgMCA5NiA5NiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDgiIGN5PSI0OCIgcj0iNDgiIGZpbGw9IiNmM2Y0ZjYiLz4KPGNpcmNsZSBjeD0iNDgiIGN5PSIzNiIgcj0iMTIiIGZpbGw9IiM5Y2EzYWYiLz4KPHBhdGggZD0iTTI0IDcyQzI0IDY1LjM3MzMgMjkuMzczMyA2MCAzNiA2MEg2MEM2Ni42MjY3IDYwIDcyIDY1LjM3MzMgNzIgNzJWNzJIMjRaIiBmaWxsPSIjOWNhM2FmIi8+Cjwvc3ZnPg=='}
          alt={profile?.closet_name || 'Closet'}
          style={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            objectFit: 'cover',
            backgroundColor: '#fff',
            border: '1px solid #e5e5e5'
          }}
          onError={(e) => { e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOTYiIGhlaWdodD0iOTYiIHZpZXdCb3g9IjAgMCA5NiA5NiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDgiIGN5PSI0OCIgcj0iNDgiIGZpbGw9IiNmM2Y0ZjYiLz4KPGNpcmNsZSBjeD0iNDgiIGN5PSIzNiIgcj0iMTIiIGZpbGw9IiM5Y2EzYWYiLz4KPHBhdGggZD0iTTI0IDcyQzI0IDY1LjM3MzMgMjkuMzczMyA2MCAzNiA2MEg2MEM2Ni42MjY3IDYwIDcyIDY1LjM3MzMgNzIgNzJWNzJIMjRaIiBmaWxsPSIjOWNhM2FmIi8+Cjwvc3ZnPg=='; }}
        />
        <div>
          <h2 style={{margin:'0 0 4px'}}>{profile?.closet_name || 'Closet'}</h2>
          <div style={{color:'#333', fontSize:14, fontWeight:500}}>
            <span>Member since: {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}</span>
            <span style={{margin:'0 0 0 12px'}}>Items: {stats.total_items}</span>
            <span style={{margin:'0 0 0 12px'}}>Rating: {stats.average_rating} ⭐ ({stats.rating_count})</span>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      {reviews && reviews.length > 0 && (
        <div className="reviews-section" style={{marginBottom: 24}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16}}>
            <h3 style={{margin:0, color:'#333'}}>Reviews ({reviews.length})</h3>
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowReviews(!showReviews)}
              style={{padding:'8px 16px', fontSize:'14px'}}
            >
              {showReviews ? 'Hide Reviews' : 'Show Reviews'}
            </button>
          </div>
          
          {showReviews && (
            <div className="reviews-list" style={{display:'flex', flexDirection:'column', gap:12}}>
              {reviews.slice(0, 5).map((review, index) => (
                <div key={review._id} className="review-item" style={{border: '1px solid #ddd', padding: '15px', borderRadius: '8px', background:'#fff'}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                    <div style={{flex:1}}>
                      <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:8}}>
                        <h5 style={{margin:0}}>Review #{index + 1}</h5>
                        <div style={{display:'flex', gap:2}}>
                          {[1,2,3,4,5].map(star => (
                            <span key={star} style={{color: star <= review.rating ? '#ffc107' : '#ddd', fontSize:'16px'}}>
                              ★
                            </span>
                          ))}
                        </div>
                        <span style={{fontSize:14, fontWeight:'bold', color:'#28a745'}}>
                          {review.rating}/5
                        </span>
                      </div>
                      <p style={{margin:'4px 0', fontSize:14}}><strong>From:</strong> {review.reviewer?.name || review.reviewer?.username || 'Unknown User'}</p>
                      {review.comment && (
                        <div style={{background:'#f8f9fa', padding:8, borderRadius:4, marginTop:8}}>
                          <p style={{margin:0, fontSize:14, fontStyle:'italic'}}>"{review.comment}"</p>
                        </div>
                      )}
                      <p style={{margin:'4px 0', fontSize:12, color:'#666'}}>
                        <strong>Created:</strong> {new Date(review.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {reviews.length > 5 && (
                <p style={{textAlign:'center', color:'#666', fontSize:14}}>
                  Showing 5 of {reviews.length} reviews
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="hats-grid">
        {items.length === 0 ? (
          <p>No items in this closet yet.</p>
        ) : (
          items.map((hat) => (
            <div key={hat._id} className="compact-hat-card">
              <div className="hat-image">
                <img 
                  src={hat.image_url} 
                  alt={hat.name}
                  loading="lazy"
                  onClick={() => openImageLightbox(hat)}
                  style={{ cursor: 'pointer' }}
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Available'; }}
                />
              </div>
              <div className="compact-hat-info">
                <h3>{hat.name}</h3>
                <p className="compact-hat-description">{hat.description}</p>
                <div className="compact-hat-details">
                  <span className="hat-brand">Brand: {hat.brand}</span>
                  <span className="hat-size">Size: {hat.size}</span>
                  <span className="hat-condition">Condition: {hat.condition}</span>
                </div>
                <div className="compact-hat-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={() => onTradeRequest && onTradeRequest(hat)}
                    style={{ backgroundColor: '#d4af37', color: 'white', border: 'none' }}
                  >
                    Request Trade
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination" style={{marginTop:16, display:'flex', gap:8}}>
          <button className="pagination-btn" disabled={page===1} onClick={() => setPage(page-1)}>← Prev</button>
          <div className="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p} className={`pagination-btn page-number ${page===p?'active':''}`} onClick={() => setPage(p)}>{p}</button>
            ))}
          </div>
          <button className="pagination-btn" disabled={page===totalPages} onClick={() => setPage(page+1)}>Next →</button>
        </div>
      )}
      
      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        itemName={lightboxItemName}
        onClose={() => setLightboxOpen(false)}
        onIndexChange={setLightboxIndex}
      />
      </div>
    </div>
  );
};

export default OwnerClosetPage;


